/**
 * Installed-app plumbing: registers the service worker and tells the page when
 * a newer build is waiting, so a tablet left open for days is not quietly
 * running last week's code.
 */
import { useEffect, useState } from 'react';

/** Fired once when an updated service worker has installed and is waiting. */
export const UPDATE_EVENT = 'lguaid:update-ready';

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  // The dev server does not serve /sw.js, and a stale worker there is a
  // debugging trap rather than a feature.
  if (import.meta.env.DEV) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        const announce = (worker: ServiceWorker | null): void => {
          if (!worker) return;
          worker.addEventListener('statechange', () => {
            // controller present means this is an update, not a first install.
            if (worker.state === 'installed' && navigator.serviceWorker.controller) {
              window.dispatchEvent(new CustomEvent(UPDATE_EVENT));
            }
          });
        };
        announce(registration.waiting);
        registration.addEventListener('updatefound', () => announce(registration.installing));
      })
      .catch(() => {
        // An app that cannot install its worker still works online; stay quiet.
      });
  });
}

/** Applies a waiting update and reloads once the new worker takes over. */
export async function applyUpdate(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration?.waiting) {
    window.location.reload();
    return;
  }
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
    once: true,
  });
  registration.waiting.postMessage('SKIP_WAITING');
}

/** True once a newer build is installed and waiting for a reload. */
export function useUpdateReady(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const onUpdate = (): void => setReady(true);
    window.addEventListener(UPDATE_EVENT, onUpdate);
    return () => window.removeEventListener(UPDATE_EVENT, onUpdate);
  }, []);
  return ready;
}

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Chrome and Edge on Android offer an install prompt we can trigger from a
 * button; iOS does not, so there the button simply never appears and the user
 * adds the app from the share sheet.
 */
export function useInstallPrompt(): { canInstall: boolean; install: () => Promise<void> } {
  const [prompt, setPrompt] = useState<InstallPromptEvent | null>(null);

  useEffect(() => {
    const onPrompt = (event: Event): void => {
      event.preventDefault();
      setPrompt(event as InstallPromptEvent);
    };
    const onInstalled = (): void => setPrompt(null);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  return {
    canInstall: prompt !== null,
    install: async () => {
      if (!prompt) return;
      await prompt.prompt();
      await prompt.userChoice;
      setPrompt(null);
    },
  };
}
